import { type ReactNode } from 'react'
import { Menu } from '@systemeio/components'
import { ChevronDownIcon } from '@systemeio/icons'
import { twJoin } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import { LocaleEnum, localeNames } from 'shared/enums/locale-enum'
import { useLanguageSwitcher } from 'shared/hooks/use-language-switcher'
import useUser from 'shared/hooks/use-user'
import { cwd } from 'shared/utils/class-name-with-modifiers'
import { getCalculatedLanguage } from 'shared/utils/get-calculated-language'
import { addPublicCustomClassNamePrefix } from 'modules/course/utils/add-class-prefix'

const locales: LocaleEnum[] = Object.values(LocaleEnum)

function LanguageSwitcher(): ReactNode {
  const { i18n } = useTranslation()
  const { user, loading } = useUser()

  const { handleChangeLanguage, isChangingLanguage } = useLanguageSwitcher()

  return (
    <div
      className={twJoin(
        'relative h-full',
        cwd(addPublicCustomClassNamePrefix('language-switcher'), {
          ['loading']: loading,
        }),
      )}
      data-test-id="header-language-switcher"
    >
      {loading ? (
        <div className="size-6 animate-pulse rounded-xs bg-bg-tertiary" />
      ) : (
        user &&
        i18n && (
          <div className="flex h-full items-center">
            <Menu
              className="max-h-[400px] min-w-40 overflow-y-auto"
              trigger={[
                <span
                  key="language"
                  className={twJoin(isChangingLanguage && `shimmer-loading`)}
                  data-test-id="header-language-switcher-current-language"
                >
                  {i18n.language ? getCalculatedLanguage(i18n.language) : ''}
                </span>,
                <ChevronDownIcon key="chevron" className="size-3 shrink-0" aria-hidden="true" />,
              ]}
              triggerProps={{ className: 'flex h-8 items-center gap-1 px-2' }}
              items={locales
                .filter(dashboardLocale => dashboardLocale !== i18n.language)
                .map(dashboardLocale => ({
                  id: dashboardLocale,
                  label: localeNames[dashboardLocale],
                  dataAttributes: {
                    'data-test-id': `header-language-switcher-language-option-${dashboardLocale}`,
                  },
                  onSelect: () => {
                    void handleChangeLanguage(dashboardLocale)
                  },
                }))}
            />
          </div>
        )
      )}
    </div>
  )
}

export function MobileLanguageSwitcher(): ReactNode {
  const { i18n } = useTranslation()
  const { user } = useUser()

  const { handleChangeLanguage, isChangingLanguage } = useLanguageSwitcher()

  if (!i18n) {
    return <div>loading...</div>
  }

  return (
    <div className="relative" data-test-id="header-language-switcher">
      <Menu
        className="max-h-[400px] min-w-40 overflow-y-auto"
        trigger={
          <button
            type="button"
            className={twJoin(
              'flex w-full items-center justify-between gap-2',
              isChangingLanguage && 'shimmer-loading',
            )}
            data-test-id="header-language-switcher-current-language"
          >
            <span className="flex-1 text-start capitalize">{user?.dashboardLocale}</span>
            <ChevronDownIcon className="size-3 shrink-0 text-icon-primary" aria-hidden="true" />
          </button>
        }
        triggerProps={{
          className: 'relative flex items-center justify-between gap-2',
        }}
        items={locales
          .filter(dashboardLocale => dashboardLocale !== i18n.language)
          .map(dashboardLocale => ({
            id: dashboardLocale,
            label: localeNames[dashboardLocale],
            dataAttributes: {
              'data-test-id': `header-language-switcher-language-option-${dashboardLocale}`,
            },
            onSelect: () => {
              void handleChangeLanguage(dashboardLocale)
            },
          }))}
      />
    </div>
  )
}

export default LanguageSwitcher
